import React from 'react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import SettingsSection from '../../SettingsSection'
import Add from '../../Controls/Add'
import DraggableSetting from '../DraggableSetting'
import NoPermissionContainer from '@SonarCX/components/molecules/NoPermissionContainer'

import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import {
  selectCategory,
  fetchCategories,
  createCategory,
  deleteCategory,
  updateCategory,
  patchCategories
} from '@SonarCX/slices/category'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { doAlphabeticalSort, doDragDrop } from '../DraggableSetting/helpers'

const CategorySettings: React.FC = () => {
  const dispatch = useAppDispatch()
  const categorySelector = useAppSelector(selectCategory)
  const { publicCategoriesValues: categories, loaded, error } = categorySelector
  const { user: currentUser } = useEnvironment()
  const [saving, setSaving] = React.useState(false)
  const hasPermission = useAccess()

  React.useEffect(() => {
    if (!categorySelector.loaded && !categorySelector.loading) {
      dispatch(fetchCategories())
    }
  }, [categorySelector, dispatch])

  const canModify = React.useCallback(
    cat => {
      if (hasPermission(PERMS.TasksAdmin)) {
        return true
      }

      if (hasPermission(PERMS.TasksOwn)) {
        const { owner_id } = cat
        return owner_id === currentUser?.id
      }

      return false
    },
    [hasPermission, currentUser]
  )

  const handleAddCategory = React.useCallback(
    async name => {
      try {
        setSaving(true)
        await dispatch(
          createCategory({ name, private: false, flow_order: null })
        )
        dispatch(AddSuccessAlert('Changes saved'))
      } catch (err) {
        console.error(err)
        dispatch(AddErrorAlert({ err }))
      } finally {
        setSaving(false)
      }
    },
    [dispatch]
  )

  const handleDeleteCategory = React.useCallback(
    obj => {
      try {
        dispatch(deleteCategory(obj.id))
        dispatch(AddSuccessAlert('Changes saved'))
      } catch (err) {
        console.error(err)
        dispatch(AddErrorAlert({ err }))
      }
    },
    [dispatch]
  )

  const handleEditCategory = React.useCallback(
    async (obj, name) => {
      try {
        await dispatch(updateCategory({ ...obj, name }))
        dispatch(AddSuccessAlert('Changes saved'))
      } catch (err) {
        console.error(err)
        dispatch(AddErrorAlert({ err }))
      }
    },
    [dispatch]
  )

  const handleSort = React.useCallback(
    (ascending: boolean) => {
      dispatch(patchCategories(doAlphabeticalSort(ascending, categories)))
    },
    [categories, dispatch]
  )

  const handleDragDrop = (
    sourceID: string,
    targetID: string,
    after?: boolean
  ) => {
    const toUpdate = doDragDrop(categories, sourceID, targetID, after)
    dispatch(patchCategories(toUpdate))
  }

  return (
    <NoPermissionContainer skipAgentPage error={error}>
      <SettingsSection title='Categories' loaded={loaded} sort={handleSort}>
        {categories.map((cat, i) => (
          <DraggableSetting
            key={cat.id}
            obj={cat}
            objType='category'
            superType='task'
            handleDelete={handleDeleteCategory}
            handleEdit={handleEditCategory}
            readOnly={!canModify(cat)}
            isLast={i === categories.length - 1}
            index={i}
            onDragDrop={handleDragDrop}
          />
        ))}

        {hasPermission([PERMS.TasksOwn, PERMS.TasksFull]) && (
          <Add
            superType='task'
            type='category'
            handleAdd={handleAddCategory}
            saving={saving}
          />
        )}
      </SettingsSection>
    </NoPermissionContainer>
  )
}

export default CategorySettings
